import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Router } from '@angular/router';
import flatpickr from 'flatpickr';
import { Italian } from 'flatpickr/dist/l10n/it';
import { Instance as FlatpickrInstance } from 'flatpickr/dist/types/instance';
import { forkJoin, of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { BaseComponent } from '../../../base/base.component';
import { FunctionService } from '../../../services/function.service';
import { AnagraphicService } from '../../../services/anagraphic.service';
import { ShipmentManagementService, ClientsBrands } from '../../../services/shipment-management.service';
import { SearchableSelectComponent, SearchableSelectOption } from '../../../components/searchable-select/searchable-select.component';
import { DamageNavBoxComponent } from './damage-nav-box/damage-nav-box.component';
import { ParcelClaimsPopupComponent } from './parcel-claims-popup/parcel-claims-popup.component';
import { Damage, DamageUserRef } from '../../../interfaces/Damages.interfaces';
import { Dealer } from '../../../interfaces/anagraphic.interfaces';
import { WaybillStatus } from '../../../interfaces/Waybills.interfaces';
import {
  DamageManagementService,
  DamageClaimsSearchRequest,
  DamageClaimsSummary,
  DamageRow,
  DamageSearchRequest
} from '../../../services/damage-management.service';

/** Colonna della tabella delle pratiche */
interface ClaimColumn {
  key: keyof Damage | 'clientName';
  label: string;
  sortable: boolean;
  type: 'text' | 'date' | 'status' | 'severity' | 'type' | 'number' | 'client' | 'user';
}

/** Colonna della tabella dei colli danneggiati (chiavi delle righe di /damaged-parcels/search) */
interface ParcelColumn {
  key: string;
  label: string;
  sortable: boolean;
  type: 'text' | 'client' | 'date' | 'number' | 'action';
}

/** Chiavi dei campi data gestiti da flatpickr */
type DateFilterKey = 'openedFrom' | 'openedTo' | 'parcelDateFrom' | 'parcelDateTo' | 'estimatedDelivery' | 'actualDelivery';

/**
 * /damage-overview: pagina principale della gestione danni, su tutti i dati.
 * Stessa struttura delle panoramiche per cliente (/damages-client-overview) e
 * per dealer (/damages-dealer-overview), senza una dimensione a fuoco:
 * - filtri completi e generici: quelli delle pratiche (codici, stato,
 *   cliente, dealer, gravità, tipo, assegnatario, apertura, scadenza) e, tra
 *   gli avanzati, quelli dei colli ereditati dalla vecchia /damaged-parcels
 *   (brand, vettore, magazzino, instradamento, date del collo e di consegna,
 *   stato bolla, destinatario, tipologia, peso, volume);
 * - resoconto delle pratiche sugli stessi filtri;
 * - card in fondo con il selettore Pratiche (principale) / Colli danneggiati,
 *   ordinamento e paginazione lato backend (endpoint /claims/search,
 *   /claims/summary e /search di DamagesController).
 * Ogni filtro è applicato alle richieste che lo supportano: le etichette
 * segnalano quelli validi solo per le pratiche o solo per i colli.
 */
@Component({
  selector: 'app-damage-overview',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, SearchableSelectComponent, DamageNavBoxComponent, ParcelClaimsPopupComponent],
  templateUrl: './damage-overview.component.html',
  styleUrls: ['./damage-overview.component.css']
})
export class DamageOverviewComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

  constructor(
    functionService: FunctionService,
    router: Router,
    private damageService: DamageManagementService,
    private anagraphicService: AnagraphicService,
    private shipmentManagementService: ShipmentManagementService
  ) {
    super(functionService, router);
    // La rotta è /damage-overview ma la funzione dei permessi (backend
    // DamagesController) si chiama "damages": BaseComponent la ricaverebbe dall'URL
    this.functionName = 'damages';
  }

  private readonly STORAGE_KEY = 'damage-overview-settings';

  // ─── SEZIONI ───
  isFiltersCollapsed         = false;
  isAdvancedFiltersCollapsed = true;
  isReportCollapsed          = false;
  isCompactTable             = false;
  /** Tabella visibile nella card in fondo: pratiche (principale) o colli danneggiati */
  activeTable: 'claims' | 'parcels' = 'claims';

  showTable(table: 'claims' | 'parcels'): void {
    this.activeTable = table;
  }

  // ─── RIQUADRO UTENTE E COLLEGAMENTI (componente condiviso nell'header) ───
  @ViewChild(DamageNavBoxComponent) navBox?: DamageNavBoxComponent;

  // ─── FILTRI PRINCIPALI (pratiche e colli) ───
  filterDamageCode  = '';
  filterParcelCode  = '';
  filterWaybillCode = '';
  // Select a scelta multipla: più valori in OR
  filterStatus:   string[] = [];
  filterClient:   string[] = [];
  /** Dealer a scelta singola (codice), '' = tutti: gli endpoint accettano un solo dealerCode */
  filterDealer      = '';
  filterSeverity: string[] = [];
  filterType:     string[] = [];
  filterAssignedTo  = '';
  onlyOverdue       = false;
  selectedOpenedFrom: Date | null = null;
  selectedOpenedTo:   Date | null = null;

  // ─── FILTRI AVANZATI (ereditati da /damaged-parcels, stessi campi di shipment-management) ───
  filterBrand:     string[] = [];
  filterCarrier:   string[] = [];
  filterWarehouse: string[] = [];
  filterRouting:   string[] = [];
  selectedParcelDateFrom:   Date | null = null;
  selectedParcelDateTo:     Date | null = null;
  selectedEstimatedDelivery: Date | null = null;
  selectedActualDelivery:    Date | null = null;
  /** Stato della bolla (WaybillStatus), '' = tutti */
  filterWaybillStatus = '';
  filterCompanyName = '';
  filterAddress     = '';
  filterCity        = '';
  filterProvince    = '';
  filterParcelType  = '';
  filterWeight      = '';
  filterWeightOperator = '=';
  filterVolume      = '';
  filterVolumeOperator = '=';

  comparisonOperators = [
    { value: '>', label: 'Maggiore di' },
    { value: '=', label: 'Uguale a' },
    { value: '<', label: 'Minore di' }
  ];

  // ─── FLATPICKR ───
  @ViewChild('openedFromInput')        openedFromInput?:        ElementRef<HTMLInputElement>;
  @ViewChild('openedToInput')          openedToInput?:          ElementRef<HTMLInputElement>;
  @ViewChild('parcelDateFromInput')    parcelDateFromInput?:    ElementRef<HTMLInputElement>;
  @ViewChild('parcelDateToInput')      parcelDateToInput?:      ElementRef<HTMLInputElement>;
  @ViewChild('estimatedDeliveryInput') estimatedDeliveryInput?: ElementRef<HTMLInputElement>;
  @ViewChild('actualDeliveryInput')    actualDeliveryInput?:    ElementRef<HTMLInputElement>;
  private pickers = new Map<DateFilterKey, FlatpickrInstance>();

  // ─── OPZIONI SELECT ───
  statusOptions: SearchableSelectOption[] = [
    { value: 'OPEN',        label: 'Aperta' },
    { value: 'IN_PROGRESS', label: 'In lavorazione' },
    { value: 'WAITING',     label: 'In attesa' },
    { value: 'CLOSED',      label: 'Chiusa' },
    { value: 'REJECTED',    label: 'Respinta' }
  ];
  severityOptions: SearchableSelectOption[] = [
    { value: 'LOW',      label: 'Bassa' },
    { value: 'MEDIUM',   label: 'Media' },
    { value: 'HIGH',     label: 'Alta' },
    { value: 'CRITICAL', label: 'Critica' }
  ];
  typeOptions: SearchableSelectOption[] = [
    { value: 'WATER',         label: 'Danno da acqua' },
    { value: 'BREAKAGE',      label: 'Rottura' },
    { value: 'MISSING_PARTS', label: 'Parti mancanti' },
    { value: 'PHYSICAL',      label: 'Danno fisico' },
    { value: 'CONTENT',       label: 'Contenuto' },
    { value: 'PACKAGING',     label: 'Imballaggio' },
    { value: 'MISSING',       label: 'Mancante' },
    { value: 'OTHER',         label: 'Altro' }
  ];
  /** Stati della bolla (stessi valori di shipment-management) */
  waybillStatusOptions: SearchableSelectOption[] = [
    { value: WaybillStatus.ACTIVE,        label: 'Attiva' },
    { value: WaybillStatus.PENDING,       label: 'In Sospeso' },
    { value: WaybillStatus.DELIVERED,     label: 'Consegnata' },
    { value: WaybillStatus.NOT_DELIVERED, label: 'Non Consegnata' },
    { value: WaybillStatus.DELETED,       label: 'Eliminata' }
  ];
  clientSelectOptions:    SearchableSelectOption[] = [];
  brandSelectOptions:     SearchableSelectOption[] = [];
  dealerSelectOptions:    SearchableSelectOption[] = [];
  carrierSelectOptions:   SearchableSelectOption[] = [];
  warehouseSelectOptions: SearchableSelectOption[] = [];
  zoneSelectOptions:      SearchableSelectOption[] = [];
  isLoadingOptions = false;

  /** Clienti e brand dell'utente (userFunctions in localStorage), inviati per le autorizzazioni */
  private clientsBrands: ClientsBrands[] = [];
  private clientNameByCode = new Map<string, string>();
  private dealerByCode     = new Map<string, Dealer>();

  // ─── RESOCONTO ───
  summary: DamageClaimsSummary = this.emptySummary();
  isLoadingSummary    = false;
  summaryErrorMessage = '';

  // ─── TABELLA PRATICHE ───
  rows: Damage[] = [];
  /** Utenti citati dalle pratiche della pagina (dal backend, per userCode): per mostrare i nomi */
  users: Record<string, DamageUserRef> = {};
  isLoading         = false;
  hasLoaded         = false;
  tableErrorMessage = '';

  sortField = 'damageOpenedAt';
  sortAsc   = false;

  currentPage  = 1;
  itemsPerPage = 25;
  totalRecords = 0;

  readonly columns: ClaimColumn[] = [
    { key: 'damageCode',        label: 'Pratica',     sortable: true, type: 'text' },
    { key: 'damageParcelCode',  label: 'Collo',       sortable: true, type: 'text' },
    { key: 'damageWaybillCode', label: 'Bolla',       sortable: true, type: 'text' },
    { key: 'clientName',        label: 'Cliente',     sortable: true, type: 'client' },
    { key: 'damageType',        label: 'Tipo',        sortable: true, type: 'type' },
    { key: 'damageStatus',      label: 'Stato',       sortable: true, type: 'status' },
    { key: 'damageSeverity',    label: 'Gravità',     sortable: true, type: 'severity' },
    { key: 'damagePriority',    label: 'Priorità',    sortable: true, type: 'number' },
    { key: 'damageAssignedTo',  label: 'Assegnata a', sortable: true, type: 'user' },
    { key: 'damageOpenedAt',    label: 'Aperta il',   sortable: true, type: 'date' },
    { key: 'damageDueDate',     label: 'Scadenza',    sortable: true, type: 'date' }
  ];

  // ─── COLLI DANNEGGIATI (seconda vista della card in fondo, endpoint /search) ───
  parcelRows: DamageRow[] = [];
  isLoadingParcels    = false;
  hasLoadedParcels    = false;
  parcelsErrorMessage = '';
  parcelSortField = 'date';
  parcelSortAsc   = false;
  parcelPage         = 1;
  parcelTotalRecords = 0;
  /** Collo di cui mostrare lo storico pratiche nel popup (null = popup chiuso) */
  claimsPopupRow: DamageRow | null = null;

  /**
   * Stesse colonne di default della vecchia /damaged-parcels. Ordinabili solo
   * le chiavi previste dal SORT_FIELD_MAP del backend (quantity è la chiave
   * che il backend mappa su parcelWeight).
   */
  readonly parcelColumns: ParcelColumn[] = [
    { key: 'parcelCode',  label: 'Collo',         sortable: true,  type: 'text' },
    { key: 'waybillCode', label: 'Bolla',         sortable: true,  type: 'text' },
    { key: 'clientName',  label: 'Cliente',       sortable: false, type: 'client' },
    { key: 'date',        label: 'Data',          sortable: true,  type: 'date' },
    { key: 'quantity',    label: 'Peso (kg)',     sortable: true,  type: 'number' },
    { key: 'volume',      label: 'Volume (m³)',   sortable: false, type: 'number' },
    { key: 'damageClaim', label: 'Pratica Danno', sortable: false, type: 'action' }
  ];

  // ─── LIFECYCLE ───
  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'auto' });
    this.loadUserPreferences();
    this.loadClients();
    this.loadOptions();
    // Pagina principale: mostra subito tutte le pratiche e tutti i colli
    this.search();
  }

  ngAfterViewInit(): void {
    this.initFlatpickr();
  }

  ngOnDestroy(): void {
    this.destroyFlatpickr();
  }

  // ─── FLATPICKR ───
  /** Campo data: input nel template, valore nel componente e ora di default quando si sceglie "oggi" */
  private get dateFields(): { key: DateFilterKey; input?: ElementRef<HTMLInputElement>; endOfDay: boolean }[] {
    return [
      { key: 'openedFrom',        input: this.openedFromInput,        endOfDay: false },
      { key: 'openedTo',          input: this.openedToInput,          endOfDay: true },
      { key: 'parcelDateFrom',    input: this.parcelDateFromInput,    endOfDay: false },
      { key: 'parcelDateTo',      input: this.parcelDateToInput,      endOfDay: true },
      { key: 'estimatedDelivery', input: this.estimatedDeliveryInput, endOfDay: false },
      { key: 'actualDelivery',    input: this.actualDeliveryInput,    endOfDay: false }
    ];
  }

  private initFlatpickr(): void {
    this.destroyFlatpickr();
    for (const field of this.dateFields) {
      if (!field.input?.nativeElement) continue;
      this.pickers.set(field.key, flatpickr(field.input.nativeElement, {
        dateFormat: 'd/m/Y H:i', enableTime: true, time_24hr: true, locale: Italian,
        defaultDate: this.getDate(field.key) ?? undefined,
        onChange: (dates: Date[]) => { this.setDate(field.key, dates[0] ?? null, false); }
      }));
    }
  }

  private destroyFlatpickr(): void {
    this.pickers.forEach(p => p.destroy());
    this.pickers.clear();
  }

  getDate(key: DateFilterKey): Date | null {
    switch (key) {
      case 'openedFrom':        return this.selectedOpenedFrom;
      case 'openedTo':          return this.selectedOpenedTo;
      case 'parcelDateFrom':    return this.selectedParcelDateFrom;
      case 'parcelDateTo':      return this.selectedParcelDateTo;
      case 'estimatedDelivery': return this.selectedEstimatedDelivery;
      case 'actualDelivery':    return this.selectedActualDelivery;
    }
  }

  /** Imposta il valore del campo data e, se richiesto, allinea il picker */
  private setDate(key: DateFilterKey, value: Date | null, syncPicker = true): void {
    switch (key) {
      case 'openedFrom':        this.selectedOpenedFrom        = value; break;
      case 'openedTo':          this.selectedOpenedTo          = value; break;
      case 'parcelDateFrom':    this.selectedParcelDateFrom    = value; break;
      case 'parcelDateTo':      this.selectedParcelDateTo      = value; break;
      case 'estimatedDelivery': this.selectedEstimatedDelivery = value; break;
      case 'actualDelivery':    this.selectedActualDelivery    = value; break;
    }
    if (!syncPicker) return;
    const picker = this.pickers.get(key);
    if (value) picker?.setDate(value, false); else picker?.clear();
  }

  /** Sposta la data di n giorni (o la imposta a oggi se vuota) */
  shiftDate(key: DateFilterKey, days: number): void {
    const next = new Date(this.getDate(key) ?? new Date());
    next.setDate(next.getDate() + days);
    this.setDate(key, next);
  }

  setDateToday(key: DateFilterKey): void {
    const today = new Date();
    const endOfDay = this.dateFields.find(f => f.key === key)?.endOfDay ?? false;
    if (endOfDay) today.setHours(23, 59, 0, 0); else today.setHours(0, 0, 0, 0);
    this.setDate(key, today);
  }

  clearDate(key: DateFilterKey): void {
    this.setDate(key, null);
  }

  // ─── DATI INIZIALI ───
  /** Clienti e brand da localStorage (stessa sorgente di shipment-management) */
  private loadClients(): void {
    const stored = localStorage.getItem('userFunctions');
    if (stored) {
      try {
        this.clientsBrands = JSON.parse(stored)?.clients ?? [];
      } catch {
        this.clientsBrands = [];
      }
    }
    this.clientSelectOptions = this.clientsBrands
      .filter(cb => cb.clientCode && cb.clientName)
      .map(cb => ({ value: cb.clientCode, label: cb.clientName! }));
    this.clientNameByCode = new Map(
      this.clientsBrands
        .filter(cb => cb.clientCode && cb.clientName)
        .map(cb => [cb.clientCode, cb.clientName!] as [string, string])
    );

    const seenBrands = new Set<string>();
    this.brandSelectOptions = this.clientsBrands
      .flatMap(cb => cb.brands ?? [])
      .filter(b => b.brandCode && b.brandName)
      .sort((a, b) => (a.brandName || '').localeCompare(b.brandName || '', 'it'))
      .filter(b => !seenBrands.has(b.brandCode) && seenBrands.add(b.brandCode))
      .map(b => ({ value: b.brandCode, label: b.brandName! }));
  }

  /**
   * Opzioni dei filtri che arrivano dal backend (dealer, magazzini,
   * instradamenti, vettori): un errore su una sorgente lascia vuota solo la
   * sua select, le altre restano usabili.
   */
  private loadOptions(): void {
    this.isLoadingOptions = true;
    forkJoin({
      dealers:    this.anagraphicService.loadDealers(this.clientsBrands).pipe(catchError(() => of([] as Dealer[]))),
      warehouses: this.anagraphicService.getAllWarehouses().pipe(catchError(() => of([] as any[]))),
      routings:   this.anagraphicService.getAllTourRoutings().pipe(catchError(() => of([] as any[]))),
      carriers:   this.shipmentManagementService.getCarriers(this.clientsBrands).pipe(catchError(() => of([] as any[])))
    }).pipe(
      finalize(() => { this.isLoadingOptions = false; })
    ).subscribe(({ dealers, warehouses, routings, carriers }) => {
      const dealerList = (Array.isArray(dealers) ? dealers : [])
        .filter(d => d?.dealerCode)
        .sort((a, b) => this.dealerLabel(a).localeCompare(this.dealerLabel(b), 'it'));
      this.dealerByCode = new Map(dealerList.map(d => [d.dealerCode, d]));
      this.dealerSelectOptions = dealerList.map(d => ({
        value: d.dealerCode,
        label: this.dealerLabel(d) + (d.dealerName ? ' (' + d.dealerCode + ')' : '')
      }));
      this.warehouseSelectOptions = (warehouses ?? []).map((w: any) => ({
        value: w.warehouseCode,
        label: w.warehouseName || w.warehouseCode
      }));
      this.zoneSelectOptions = (routings ?? []).map((r: any) => ({
        value: r.tourRoutingCode,
        label: r.tourRoutingCode
      }));
      this.carrierSelectOptions = (carriers ?? []).map((c: any) => ({
        value: c.carrierCode,
        label: c.carrierName || c.carrierCode
      }));
    });
  }

  /** Nome del dealer dall'anagrafica, altrimenti il codice */
  private dealerLabel(dealer: Dealer): string {
    return dealer.dealerName?.trim() || dealer.dealerCode;
  }

  // ─── STRUMENTARIO ───
  /** Ri-esegue la ricerca con i filtri correnti, restando sulla pagina corrente */
  refreshData(): void {
    if (this.isLoading) return;
    this.navBox?.refresh();
    this.loadData();
  }

  get areAllSectionsCollapsed(): boolean {
    return this.isFiltersCollapsed && this.isReportCollapsed;
  }

  toggleAllSections(): void {
    const collapse = !this.areAllSectionsCollapsed;
    this.isFiltersCollapsed = collapse;
    this.isReportCollapsed  = collapse;
    if (!collapse) setTimeout(() => this.initFlatpickr());
  }

  toggleFiltersCollapsed(): void {
    this.isFiltersCollapsed = !this.isFiltersCollapsed;
    // I campi data vengono ricreati dal *ngIf: flatpickr va reinizializzato
    if (!this.isFiltersCollapsed) setTimeout(() => this.initFlatpickr());
  }

  toggleAdvancedFilters(): void {
    this.isAdvancedFiltersCollapsed = !this.isAdvancedFiltersCollapsed;
    // I campi data avanzati vengono ricreati dal *ngIf: flatpickr va reinizializzato
    if (!this.isAdvancedFiltersCollapsed) setTimeout(() => this.initFlatpickr());
  }

  toggleReportCollapsed(): void {
    this.isReportCollapsed = !this.isReportCollapsed;
  }

  toggleCompactTable(): void {
    this.isCompactTable = !this.isCompactTable;
    this.saveUserPreferences();
  }

  private loadUserPreferences(): void {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return;
      const settings = JSON.parse(raw);
      if (typeof settings.isCompactTable === 'boolean') {
        this.isCompactTable = settings.isCompactTable;
      }
      if (typeof settings.itemsPerPage === 'number' && settings.itemsPerPage > 0) {
        this.itemsPerPage = settings.itemsPerPage;
      }
    } catch (error) {
      console.warn('Error loading damage overview preferences:', error);
    }
  }

  private saveUserPreferences(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify({
        isCompactTable: this.isCompactTable,
        itemsPerPage:   this.itemsPerPage
      }));
    } catch (error) {
      console.warn('Error saving damage overview preferences:', error);
    }
  }

  // ─── RICERCA ───
  search(): void {
    this.currentPage = 1;
    this.parcelPage  = 1;
    this.loadData();
  }

  /** Numero di filtri avanzati valorizzati (badge sul bottone "Filtri avanzati") */
  get advancedFiltersCount(): number {
    const values: unknown[] = [
      this.filterBrand.length, this.filterCarrier.length, this.filterWarehouse.length, this.filterRouting.length,
      this.selectedParcelDateFrom, this.selectedParcelDateTo, this.selectedEstimatedDelivery, this.selectedActualDelivery,
      this.filterWaybillStatus, this.filterCompanyName.trim(), this.filterAddress.trim(), this.filterCity.trim(),
      this.filterProvince.trim(), this.filterParcelType.trim(), this.filterWeight.trim(), this.filterVolume.trim()
    ];
    return values.filter(v => !!v).length;
  }

  /** Filtri delle pratiche (/claims/search e /claims/summary) */
  private buildSearchRequest(): DamageClaimsSearchRequest {
    const request: DamageClaimsSearchRequest = {
      page:      this.currentPage - 1,
      size:      this.itemsPerPage,
      sortField: this.sortField,
      sortAsc:   this.sortAsc
    };
    if (this.filterDamageCode.trim())  request.damageCode   = this.filterDamageCode.trim();
    if (this.filterParcelCode.trim())  request.parcelCode   = this.filterParcelCode.trim();
    if (this.filterWaybillCode.trim()) request.waybillCode  = this.filterWaybillCode.trim();
    if (this.filterStatus.length)      request.statuses     = [...this.filterStatus];
    if (this.filterSeverity.length)    request.severities   = [...this.filterSeverity];
    if (this.filterType.length)        request.types        = [...this.filterType];
    if (this.filterClient.length)      request.clientCodes  = [...this.filterClient];
    if (this.filterBrand.length)       request.brandCodes   = [...this.filterBrand];
    if (this.filterCarrier.length)     request.carrierCodes = [...this.filterCarrier];
    if (this.filterDealer)             request.dealerCode   = this.filterDealer;
    if (this.filterAssignedTo.trim())  request.assignedTo   = this.filterAssignedTo.trim();
    if (this.selectedOpenedFrom)       request.openedFrom   = this.selectedOpenedFrom.toISOString();
    if (this.selectedOpenedTo)         request.openedTo     = this.selectedOpenedTo.toISOString();
    if (this.onlyOverdue)              request.onlyOverdue  = true;
    return request;
  }

  /**
   * Filtri dei colli (/damaged-parcels/search). Codici, cliente, dealer,
   * brand e vettore sono in comune con le pratiche; lo stato pratica passa
   * solo se ne è scelto uno (il backend ne accetta uno solo). Gravità, tipo,
   * assegnatario, date di apertura e scadenza riguardano solo le pratiche e
   * non vengono applicati ai colli; i filtri avanzati sui colli valgono solo qui.
   */
  private buildParcelSearchRequest(): DamageSearchRequest {
    const request: DamageSearchRequest = {
      clientCode:    this.filterClient.length === 1 ? this.filterClient[0] : null,
      warehouseCode: this.filterWarehouse.length === 1 ? this.filterWarehouse[0] : null,
      dateFrom:      this.selectedParcelDateFrom ? this.selectedParcelDateFrom.toISOString() : null,
      dateTo:        this.selectedParcelDateTo   ? this.selectedParcelDateTo.toISOString()   : null,
      page:          this.parcelPage - 1,
      size:          this.itemsPerPage,
      sortField:     this.parcelSortField,
      sortAsc:       this.parcelSortAsc
    };
    if (this.filterParcelCode.trim()) {
      request.parcelCode = this.filterParcelCode.trim();
      // Ricerca mirata sul singolo collo: il backend restituisce il collo anche
      // se non è marcato come danneggiato (come faceva /damaged-parcels)
      request.includeNotDamaged = true;
    }
    if (this.filterWaybillCode.trim())  request.waybillCode    = this.filterWaybillCode.trim();
    if (this.filterDealer)              request.dealerCode     = this.filterDealer;
    if (this.filterWaybillStatus)       request.status         = this.filterWaybillStatus;
    if (this.filterStatus.length === 1) request.claimStatus    = this.filterStatus[0];
    if (this.filterClient.length)       request.clientCodes    = [...this.filterClient];
    if (this.filterBrand.length)        request.brandCodes     = [...this.filterBrand];
    if (this.filterCarrier.length)      request.carrierCodes   = [...this.filterCarrier];
    if (this.filterWarehouse.length)    request.warehouseCodes = [...this.filterWarehouse];
    if (this.filterRouting.length)      request.zones          = [...this.filterRouting];
    if (this.selectedEstimatedDelivery) request.estimatedDeliveryDate = this.selectedEstimatedDelivery.toISOString();
    if (this.selectedActualDelivery)    request.actualDeliveryDate    = this.selectedActualDelivery.toISOString();
    if (this.filterCompanyName.trim())  request.companyName = this.filterCompanyName.trim();
    if (this.filterAddress.trim())      request.address     = this.filterAddress.trim();
    if (this.filterCity.trim())         request.city        = this.filterCity.trim();
    if (this.filterProvince.trim())     request.province    = this.filterProvince.trim();
    if (this.filterParcelType.trim())   request.parcelType  = this.filterParcelType.trim();
    if (this.filterWeight.trim()) {
      request.weight         = this.filterWeight.trim();
      request.weightOperator = this.filterWeightOperator;
    }
    if (this.filterVolume.trim()) {
      request.volume         = this.filterVolume.trim();
      request.volumeOperator = this.filterVolumeOperator;
    }
    return request;
  }

  private loadData(): void {
    this.loadSummary();
    this.loadClaims();
    this.loadParcels();
  }

  /** Pagina corrente delle pratiche (filtri, ordinamento e paginazione lato backend) */
  private loadClaims(): void {
    this.isLoading         = true;
    this.tableErrorMessage = '';
    this.damageService.searchClaims(this.buildSearchRequest(), this.clientsBrands).pipe(
      finalize(() => { this.isLoading = false; this.hasLoaded = true; })
    ).subscribe({
      next: res => {
        this.rows         = res.content;
        this.users        = res.users;
        this.totalRecords = res.totalElements;
      },
      error: () => {
        this.rows              = [];
        this.users             = {};
        this.totalRecords      = 0;
        this.tableErrorMessage = 'Errore nel caricamento delle pratiche. Riprova più tardi.';
      }
    });
  }

  /** Resoconto sugli stessi filtri della ricerca, così le cifre corrispondono all'elenco */
  private loadSummary(): void {
    this.isLoadingSummary    = true;
    this.summaryErrorMessage = '';
    this.damageService.getClaimsSummary(this.buildSearchRequest(), this.clientsBrands).pipe(
      finalize(() => { this.isLoadingSummary = false; })
    ).subscribe({
      next: summary => { this.summary = summary; },
      error: () => {
        this.summary             = this.emptySummary();
        this.summaryErrorMessage = 'Errore nel caricamento del resoconto.';
      }
    });
  }

  retrySummary(): void {
    if (this.isLoadingSummary) return;
    this.loadSummary();
  }

  private emptySummary(): DamageClaimsSummary {
    return { total: 0, open: 0, inProgress: 0, waiting: 0, closed: 0, rejected: 0, overdue: 0 };
  }

  resetFilters(): void {
    this.filterDamageCode  = '';
    this.filterParcelCode  = '';
    this.filterWaybillCode = '';
    this.filterStatus      = [];
    this.filterClient      = [];
    this.filterDealer      = '';
    this.filterSeverity    = [];
    this.filterType        = [];
    this.filterAssignedTo  = '';
    this.onlyOverdue       = false;
    this.filterBrand       = [];
    this.filterCarrier     = [];
    this.filterWarehouse   = [];
    this.filterRouting     = [];
    this.filterWaybillStatus = '';
    this.filterCompanyName = '';
    this.filterAddress     = '';
    this.filterCity        = '';
    this.filterProvince    = '';
    this.filterParcelType  = '';
    this.filterWeight      = '';
    this.filterWeightOperator = '=';
    this.filterVolume      = '';
    this.filterVolumeOperator = '=';
    for (const field of this.dateFields) this.setDate(field.key, null);
    this.sortField = 'damageOpenedAt';
    this.sortAsc   = false;
    this.parcelSortField = 'date';
    this.parcelSortAsc   = false;
    this.search();
  }

  // ─── ORDINAMENTO PRATICHE (lato backend) ───
  /** Campo di ordinamento inviato al backend per una colonna (il cliente è ordinato per codice) */
  private sortKeyOf(col: ClaimColumn): string {
    return col.key === 'clientName' ? 'damageClientCode' : col.key;
  }

  sortBy(col: ClaimColumn): void {
    if (!col.sortable) return;
    const field = this.sortKeyOf(col);
    if (this.sortField === field) { this.sortAsc = !this.sortAsc; }
    else { this.sortField = field; this.sortAsc = true; }
    this.loadClaims();
  }

  getSortIcon(col: ClaimColumn): string {
    if (this.sortField !== this.sortKeyOf(col)) return 'fas fa-sort text-muted';
    return this.sortAsc ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary';
  }

  // ─── PAGINAZIONE PRATICHE ───
  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalRecords / this.itemsPerPage));
  }

  /** Numeri di pagina mostrati nella barra (finestra di 5 attorno alla corrente) */
  private pageNumbersFor(current: number, totalPages: number): number[] {
    const maxButtons = 5;
    let start = Math.max(1, current - Math.floor(maxButtons / 2));
    const end = Math.min(totalPages, start + maxButtons - 1);
    if (end - start + 1 < maxButtons) {
      start = Math.max(1, end - maxButtons + 1);
    }
    const pages: number[] = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  }

  getPageItems(): (number | null)[] {
    return this.pageItemsFor(this.currentPage, this.totalPages);
  }

  /** Numeri di pagina con le pagine estreme e i puntini (null) quando ce ne sono altre oltre la finestra */
  private pageItemsFor(current: number, total: number): (number | null)[] {
    const window = this.pageNumbersFor(current, total);
    if (window.length === 0) return [];
    const items: (number | null)[] = [];
    const first = window[0];
    const last = window[window.length - 1];
    if (first > 1) {
      items.push(1);
      if (first > 2) items.push(null);
    }
    items.push(...window);
    if (last < total) {
      if (last < total - 1) items.push(null);
      items.push(total);
    }
    return items;
  }

  trackByPageItem(index: number, item: number | null): number {
    return item ?? -index - 1;
  }

  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.itemsPerPage + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.itemsPerPage, this.totalRecords);
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadClaims();
  }

  // Vai a pagina
  isPageJumpOpen = false;
  pageJumpValue = '';
  @ViewChild('pageJumpInput') pageJumpInput?: ElementRef<HTMLInputElement>;
  // Vai all'elemento
  isItemJumpOpen = false;
  itemJumpValue = '';
  @ViewChild('itemJumpInput') itemJumpInput?: ElementRef<HTMLInputElement>;

  openPageJump(): void {
    this.pageJumpValue = '';
    this.isPageJumpOpen = true;
    setTimeout(() => this.pageJumpInput?.nativeElement.focus());
  }

  closePageJump(): void {
    this.isPageJumpOpen = false;
  }

  applyPageJump(): void {
    const n = parseInt(this.pageJumpValue, 10);
    this.closePageJump();
    if (!isNaN(n)) {
      this.onPageChange(Math.min(Math.max(n, 1), this.totalPages));
    }
  }

  openItemJump(): void {
    this.itemJumpValue = '';
    this.isItemJumpOpen = true;
    setTimeout(() => this.itemJumpInput?.nativeElement.focus());
  }

  closeItemJump(): void {
    this.isItemJumpOpen = false;
  }

  applyItemJump(): void {
    const n = parseInt(this.itemJumpValue, 10);
    this.closeItemJump();
    if (!isNaN(n)) {
      const item = Math.min(Math.max(n, 1), Math.max(1, this.totalRecords));
      this.onPageChange(Math.ceil(item / this.itemsPerPage));
    }
  }

  /** Elementi per pagina condivisi dalle tabelle Pratiche e Colli */
  setItemsPerPage(n: number): void {
    if (this.itemsPerPage === n) return;
    this.itemsPerPage = n;
    this.currentPage  = 1;
    this.parcelPage   = 1;
    this.saveUserPreferences();
    this.loadClaims();
    this.loadParcels();
  }

  // ─── COLLI DANNEGGIATI ───
  /** Pagina corrente dei colli danneggiati (paginazione e ordinamento lato backend) */
  private loadParcels(): void {
    this.isLoadingParcels    = true;
    this.parcelsErrorMessage = '';
    this.damageService.search(this.buildParcelSearchRequest(), this.clientsBrands).pipe(
      finalize(() => { this.isLoadingParcels = false; this.hasLoadedParcels = true; })
    ).subscribe({
      next: res => {
        this.parcelRows         = res.content;
        this.parcelTotalRecords = res.totalElements;
      },
      error: () => {
        this.parcelRows          = [];
        this.parcelTotalRecords  = 0;
        this.parcelsErrorMessage = 'Errore nel caricamento dei colli danneggiati. Riprova più tardi.';
      }
    });
  }

  retryParcels(): void {
    if (this.isLoadingParcels) return;
    this.loadParcels();
  }

  sortParcelsBy(col: ParcelColumn): void {
    if (!col.sortable) return;
    if (this.parcelSortField === col.key) { this.parcelSortAsc = !this.parcelSortAsc; }
    else { this.parcelSortField = col.key; this.parcelSortAsc = true; }
    this.loadParcels();
  }

  getParcelSortIcon(col: ParcelColumn): string {
    if (this.parcelSortField !== col.key) return 'fas fa-sort text-muted';
    return this.parcelSortAsc ? 'fas fa-sort-up text-primary' : 'fas fa-sort-down text-primary';
  }

  get parcelTotalPages(): number {
    return Math.max(1, Math.ceil(this.parcelTotalRecords / this.itemsPerPage));
  }

  getParcelPageItems(): (number | null)[] {
    return this.pageItemsFor(this.parcelPage, this.parcelTotalPages);
  }

  get parcelRangeStart(): number {
    return this.parcelTotalRecords === 0 ? 0 : (this.parcelPage - 1) * this.itemsPerPage + 1;
  }

  get parcelRangeEnd(): number {
    return Math.min(this.parcelPage * this.itemsPerPage, this.parcelTotalRecords);
  }

  onParcelPageChange(page: number): void {
    if (page < 1 || page > this.parcelTotalPages || page === this.parcelPage) return;
    this.parcelPage = page;
    this.loadParcels();
  }

  parcelRowNumber(index: number): number {
    return (this.parcelPage - 1) * this.itemsPerPage + index + 1;
  }

  parcelClientName(row: DamageRow): string {
    const code = (row['clientCode'] ?? '') as string;
    return this.clientNameByCode.get(code) ?? code;
  }

  /** Tutte le pratiche del collo (storico), dalla più recente */
  claimsOf(row: DamageRow): Damage[] {
    return Array.isArray(row['damageClaims']) ? row['damageClaims'] : [];
  }

  /** Collo senza pratiche: apre la creazione di una nuova pratica danno */
  openNewClaim(row: DamageRow, event?: Event): void {
    event?.stopPropagation();
    this.router.navigate(['/damage-overview/create', row['parcelCode'] || '']);
  }

  /** Collo con una sola pratica (aperta o chiusa): apre direttamente il suo dettaglio */
  openSingleClaim(row: DamageRow, event?: Event): void {
    event?.stopPropagation();
    const claim = this.claimsOf(row)[0];
    if (claim?.damageId != null) {
      this.router.navigate(['/damage-overview/detail', Number(claim.damageId)]);
    }
  }

  /** Riga del collo: una pratica apre il dettaglio, più pratiche aprono il popup, nessuna la creazione */
  openParcel(row: DamageRow): void {
    const count = this.claimsOf(row).length;
    if (count === 1) this.openSingleClaim(row);
    else if (count > 1) this.openClaimsPopup(row);
    else this.openNewClaim(row);
  }

  openClaimsPopup(row: DamageRow, event?: Event): void {
    event?.stopPropagation();
    this.claimsPopupRow = row;
  }

  closeClaimsPopup(): void {
    this.claimsPopupRow = null;
  }

  /** Dal popup: apre il dettaglio della pratica scelta */
  openClaimFromPopup(damageId: number): void {
    this.claimsPopupRow = null;
    this.router.navigate(['/damage-overview/detail', damageId]);
  }

  trackByParcel(index: number, row: DamageRow): string {
    return (row['parcelCode'] as string) || String(index);
  }

  // ─── RIGHE PRATICHE ───
  openDetail(damage: Damage): void {
    this.router.navigate(['/damage-overview/detail', Number(damage.damageId)]);
  }

  isClosed(damage: Damage): boolean {
    return damage.damageStatus === 'CLOSED' || damage.damageStatus === 'REJECTED';
  }

  isOverdue(damage: Damage): boolean {
    if (this.isClosed(damage) || !damage.damageDueDate) return false;
    const due = new Date(damage.damageDueDate);
    return !isNaN(due.getTime()) && due.getTime() < Date.now();
  }

  /** Numero progressivo della riga nell'intero elenco (non solo nella pagina) */
  rowNumber(index: number): number {
    return (this.currentPage - 1) * this.itemsPerPage + index + 1;
  }

  /** Valore di una colonna data (stringa ISO dal backend), null se vuoto */
  dateValue(damage: Damage, col: ClaimColumn): string | null {
    const v = this.cellValue(damage, col);
    return typeof v === 'string' && v ? v : null;
  }

  /** Valore grezzo della cella (il nome cliente è risolto a parte) */
  cellValue(damage: Damage, col: ClaimColumn): unknown {
    return col.key === 'clientName' ? this.clientName(damage) : damage[col.key];
  }

  clientName(damage: Damage): string {
    const code = damage.damageClientCode ?? '';
    return this.clientNameByCode.get(code) ?? code;
  }

  /**
   * Nome dell'utente dal suo codice (mappa `users` della pagina); se il
   * backend non lo ha risolto resta il codice. Null se il codice è vuoto.
   */
  userName(userCode?: string | null): string | null {
    if (!userCode) return null;
    const ref = this.users[userCode] ?? this.users[userCode.trim()];
    return ref?.userName?.trim() || userCode;
  }

  statusBadgeClass(status?: string): string {
    switch (status) {
      case 'OPEN':        return 'dmg-badge--info';
      case 'IN_PROGRESS': return 'dmg-badge--pending';
      case 'WAITING':     return 'dmg-badge--medium';
      case 'CLOSED':      return 'dmg-badge--resolved';
      case 'REJECTED':    return 'dmg-badge--critical';
      default:            return 'dmg-badge--info';
    }
  }

  severityBadgeClass(severity?: string): string {
    switch (severity) {
      case 'CRITICAL': return 'dmg-badge--critical';
      case 'HIGH':     return 'dmg-badge--high';
      case 'MEDIUM':   return 'dmg-badge--medium';
      case 'LOW':      return 'dmg-badge--low';
      default:         return 'dmg-badge--info';
    }
  }

  trackByDamage(index: number, damage: Damage): number {
    return damage.damageId ?? index;
  }
}
